// Parse a Letterboxd export zip into ExportTables (§8.4).
// Shared by the Web Worker (browser) and the fixture/seed scripts (Bun), so it
// takes an ArrayBuffer/Uint8Array and returns tables — no DOM dependencies.

import JSZip from "jszip";
import Papa from "papaparse";
import type { CsvRow, ExportTables } from "./distiller.ts";

export class NotAnExportError extends Error {
  constructor() {
    super("That zip doesn't look like a Letterboxd export.");
    this.name = "NotAnExportError";
  }
}

export interface ParseProgress {
  file: string;
  rows: number;
}

const parseCsv = (text: string): CsvRow[] => {
  const res = Papa.parse<CsvRow>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });
  return res.data.filter((r) => r && Object.keys(r).length > 0);
};

// Match a file inside the zip regardless of nesting / rename, by its basename.
const findFile = (zip: JSZip, basename: string): JSZip.JSZipObject | null => {
  const target = basename.toLowerCase();
  for (const name of Object.keys(zip.files)) {
    const base = name.split("/").pop()?.toLowerCase() ?? "";
    if (base === target) return zip.files[name];
  }
  return null;
};

export async function parseZip(
  data: ArrayBuffer | Uint8Array,
  onProgress?: (p: ParseProgress) => void,
): Promise<ExportTables> {
  const zip = await JSZip.loadAsync(data);

  const readTable = async (basename: string): Promise<CsvRow[] | undefined> => {
    const f = findFile(zip, basename);
    if (!f) return undefined;
    const text = await f.async("string");
    const rows = parseCsv(text);
    onProgress?.({ file: basename, rows: rows.length });
    return rows;
  };

  const [watched, ratings, watchlist, diary, profile] = await Promise.all([
    readTable("watched.csv"),
    readTable("ratings.csv"),
    readTable("watchlist.csv"),
    readTable("diary.csv"),
    readTable("profile.csv"),
  ]);

  // Validate: presence of watched.csv or ratings.csv (§8.4).
  if (!watched && !ratings) {
    throw new NotAnExportError();
  }

  // Letterboxd account username (profile.csv → Username), for deep links.
  const username = ((profile?.[0]?.["Username"] ?? profile?.[0]?.["username"]) as string | undefined)?.trim() || undefined;

  return { watched, ratings, watchlist, diary, username };
}
